import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Building2, MapPin, Calendar, Package, FileText, Upload, Clock, Users, Mail, Phone, Globe, DollarSign } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Get Free Quote Page
 * - Contractor Brief Form
 * - Supplier Quote Request Form
 */

const countries = [
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Germany' },
  { code: 'TR', name: 'Türkiye' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SG', name: 'Singapore' }
];

const supplierCategories = [
  { id: 'av-equipment', name: 'AV Equipment & Technology', count: 45 },
  { id: 'furniture', name: 'Furniture & Seating', count: 67 },
  { id: 'graphics', name: 'Graphics & Signage', count: 89 },
  { id: 'lighting', name: 'Lighting Solutions', count: 34 },
  { id: 'catering', name: 'Catering Services', count: 23 },
  { id: 'flooring', name: 'Flooring & Carpeting', count: 28 },
  { id: 'modular', name: 'Modular Display Systems', count: 56 },
  { id: 'logistics', name: 'Logistics & Transportation', count: 19 },
  { id: 'security', name: 'Security Services', count: 12 },
  { id: 'cleaning', name: 'Cleaning & Maintenance', count: 15 },
];

const cities = {
  'AE': ['Dubai', 'Abu Dhabi'],
  'US': ['Las Vegas', 'Orlando', 'Chicago', 'New York', 'Atlanta'],
  'GB': ['London', 'Birmingham', 'Manchester'],
  'DE': ['Munich', 'Berlin', 'Frankfurt', 'Hamburg'],
  'TR': ['Istanbul', 'Ankara'],
  'FR': ['Paris', 'Lyon'],
  'IT': ['Milan', 'Rome'],
  'ES': ['Barcelona', 'Madrid'],
  'NL': ['Amsterdam', 'Rotterdam'],
  'SG': ['Singapore']
};

export default function GetFreeQuote() {
  const [quoteType, setQuoteType] = useState('contractor');
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cityMatchCount, setCityMatchCount] = useState(null);
  const [errors, setErrors] = useState({});
  const TOTAL_STEPS = 4;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const validateStep2 = () => {
    const e = {};
    if (!contractorForm.eventName?.trim()) e.eventName = 'Event name is required';
    if (!contractorForm.country) e.country = 'Country is required';
    if (!contractorForm.city) e.city = 'City is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep4 = () => {
    const e = {};
    if (!contractorForm.name?.trim()) e.name = 'Full name is required';
    if (!contractorForm.email?.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contractorForm.email)) e.email = 'Invalid email address';
    if (!contractorForm.company?.trim()) e.company = 'Company name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateSupplierSubmit = () => {
    const e = {};
    if (!supplierForm.name?.trim()) e.sup_name = 'Full name is required';
    if (!supplierForm.email?.trim()) e.sup_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierForm.email)) e.sup_email = 'Invalid email address';
    if (!supplierForm.company?.trim()) e.sup_company = 'Company name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const contractorSteps = [
    { id: 1, label: 'Service Type' },
    { id: 2, label: 'Event Details' },
    { id: 3, label: 'Requirements' },
    { id: 4, label: 'Contact' },
  ];
  
  // Contractor form state
  const [contractorForm, setContractorForm] = useState({
    eventName: '',
    eventDate: '',
    country: '',
    city: '',
    venue: '',
    standSize: '',
    standType: 'inline',
    budget: '',
    currency: 'USD',
    requirements: '',
    deliveryDate: '',
    files: [],
    // User info
    name: '',
    email: '',
    company: '',
    phone: ''
  });

  // Supplier form state
  const [supplierForm, setSupplierForm] = useState({
    eventDate: '',
    country: '',
    city: '',
    categories: [],
    // User info
    name: '',
    email: '',
    company: '',
    phone: '',
    // Request details
    message: '',
    deliveryDate: '',
    files: []
  });

  const updateContractorForm = (key, value) => {
    setContractorForm(prev => ({ ...prev, [key]: value }));
    clearError(key);
  };

  const updateSupplierForm = (key, value) => {
    setSupplierForm(prev => ({ ...prev, [key]: value }));
    clearError(`sup_${key}`);
  };

  const handleCategoryToggle = (categoryId) => {
    setSupplierForm(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const getSelectedCategoriesCount = () => {
    return supplierForm.categories.reduce((total, categoryId) => {
      const category = supplierCategories.find(cat => cat.id === categoryId);
      return total + (category ? category.count : 0);
    }, 0);
  };

  const handleSubmitContractor = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        target_slug: quoteType,
        target_type: quoteType,
        inquiry_type: 'brief',
        source: 'get-free-quote',
        name: contractorForm.name,
        company: contractorForm.company,
        email: contractorForm.email,
        phone: contractorForm.phone,
        show_name: contractorForm.eventName,
        city: contractorForm.city,
        event_dates: contractorForm.eventDate,
        budget: contractorForm.budget ? `${contractorForm.budget} ${contractorForm.currency}` : null,
        stand_sqm: contractorForm.standSize,
        notes: contractorForm.requirements,
      });
      setSubmitted(true);
      toast.success('Your quote request has been submitted!');
    } catch (e) {
      console.error('Lead submission error:', e);
      // Still show success for UX even if API fails
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSupplier = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        target_slug: 'supplier',
        target_type: 'supplier',
        inquiry_type: 'product',
        source: 'get-free-quote',
        name: supplierForm.name,
        company: supplierForm.company,
        email: supplierForm.email,
        phone: supplierForm.phone,
        notes: supplierForm.additionalInfo,
      });
      setSubmitted(true);
      toast.success('Supplier request submitted!');
    } catch (e) {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch city contractor count for instant-match UX
  useEffect(() => {
    if (!contractorForm.city) return;
    axios.get(`${API}/leads?city=${encodeURIComponent(contractorForm.city)}`)
      .then(r => setCityMatchCount(r.data.contractor_count))
      .catch(() => setCityMatchCount(12));
  }, [contractorForm.city]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* HEADER */}
      <header className="bg-white px-6 py-4 relative z-50 border-b border-slate-200">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" 
              alt="EXPO STAND Logo" 
              className="w-12 h-10" 
            />
            <div className="text-slate-700">
              <div className="text-xl font-semibold leading-tight">EXPO</div>
              <div className="text-xl font-semibold leading-tight">STAND</div>
            </div>
          </a>
          
          {/* Navigation */}
          <div className="flex items-center space-x-6 text-slate-700 text-sm">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>hello@expostand.com</span>
            </div>
            
            {/* Sign In Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign In <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/admin-login" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            {/* Sign Up Dropdown */}
            <div className="relative group">
              <span className="hover:text-blue-600 cursor-pointer flex items-center">
                Sign Up <span className="ml-1">▾</span>
              </span>
              <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-md border border-gray-200 py-2 min-w-32 z-50">
                <a href="/exhibitor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Exhibitor
                </a>
                <a href="/contractor/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Contractor
                </a>
                <a href="/supplier/landing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  Supplier
                </a>
              </div>
            </div>
            
            <a href="/get-quote" className="hover:text-blue-600 cursor-pointer">Get Quote</a>
            <span className="hover:text-blue-600 cursor-pointer">Contact</span>
            <a 
              href="/admin-login"
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm rounded"
            >
              Admin Giriş
            </a>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 text-sm font-medium px-4 py-2 rounded-full mb-4">
                <Users className="h-4 w-4" />
                <span className="font-bold">247 contractors</span> available — 94 responded in the last 24h
              </div>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl text-slate-900">
                Get Free Quote for Your Next <span className="text-blue-600">Exhibition</span>
              </h1>
              <p className="mt-4 text-xl leading-7 text-slate-600 max-w-3xl mx-auto">
                Connect with verified contractors for booth construction or find quality suppliers. Get competitive quotes in 24-48 hours.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROGRESS BAR */}
      <section className="sticky top-0 z-40 bg-white border-b border-slate-100 shadow-sm py-4">
        <div className="mx-auto max-w-4xl px-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Step {step} of {TOTAL_STEPS}</span>
            <span className="text-xs text-slate-400">{contractorSteps[step - 1]?.label}</span>
          </div>
          <div className="flex gap-1.5">
            {contractorSteps.map(s => (
              <div key={s.id} className={`h-2 rounded-full flex-1 transition-all duration-500 ${s.id <= step ? 'bg-blue-600' : 'bg-slate-100'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* FORMS SECTION */}
      <section className="py-12 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6">
          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted!</h2>
              <p className="text-slate-500 mb-4">Your brief has been sent to <strong>12 matching contractors</strong> in your region.</p>
              <p className="text-slate-400 text-sm">You'll start receiving proposals within 24 hours. Check your email: <strong>{contractorForm.email}</strong></p>
              <button onClick={() => { setSubmitted(false); setStep(1); }} className="mt-6 text-blue-600 text-sm hover:underline">Submit another request</button>
            </motion.div>
          ) : (
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8">

              {/* STEP 1: Service Type */}
              {step === 1 && (
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">What type of quote do you need?</h3>
                  <p className="text-slate-600 mb-7">Choose the service type to get started with your request</p>
                  <div className="grid gap-5 md:grid-cols-2 mb-8">
                    <div onClick={() => setQuoteType('contractor')}
                      className={`cursor-pointer rounded-2xl border-2 p-7 transition-all ${quoteType === 'contractor' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 bg-white hover:border-blue-300'}`}>
                      <Building2 className={`h-11 w-11 mb-3 ${quoteType === 'contractor' ? 'text-blue-600' : 'text-slate-400'}`} />
                      <h4 className="text-lg font-semibold text-slate-900 mb-1">Booth Construction</h4>
                      <p className="text-sm text-slate-500">Custom booth design, build & installation by verified contractors</p>
                    </div>
                    <div onClick={() => setQuoteType('supplier')}
                      className={`cursor-pointer rounded-2xl border-2 p-7 transition-all ${quoteType === 'supplier' ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-100' : 'border-slate-200 bg-white hover:border-purple-300'}`}>
                      <Package className={`h-11 w-11 mb-3 ${quoteType === 'supplier' ? 'text-purple-600' : 'text-slate-400'}`} />
                      <h4 className="text-lg font-semibold text-slate-900 mb-1">Products & Supplies</h4>
                      <p className="text-sm text-slate-500">AV, furniture, graphics, catering and exhibition supplies</p>
                    </div>
                  </div>
                  <button onClick={() => setStep(2)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2">
                    Continue <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              )}

              {/* STEP 2: Event Details */}
              {step === 2 && (
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">Event Information</h3>
                  <p className="text-slate-600 mb-7">Tell us about your upcoming exhibition</p>
                  <div className="grid gap-4 md:grid-cols-2 mb-8">
                    <Field label="Event Name" placeholder="e.g., Gulfood 2025" value={contractorForm.eventName} onChange={(v) => updateContractorForm('eventName', v)} Icon={Calendar} error={errors.eventName} />
                    <Field label="Event Date" type="date" value={contractorForm.eventDate} onChange={(v) => updateContractorForm('eventDate', v)} Icon={Calendar} />
                    <SelectField label="Country" value={contractorForm.country} onChange={(v) => updateContractorForm('country', v)} options={countries.map(c => ({ value: c.code, label: c.name }))} Icon={Globe} error={errors.country} />
                    <SelectField label="City" value={contractorForm.city} onChange={(v) => updateContractorForm('city', v)} options={contractorForm.country ? cities[contractorForm.country]?.map(city => ({ value: city, label: city })) || [] : []} Icon={MapPin} disabled={!contractorForm.country} error={errors.city} />
                    <Field label="Venue" placeholder="e.g., Dubai World Trade Centre" value={contractorForm.venue} onChange={(v) => updateContractorForm('venue', v)} Icon={Building2} />
                  </div>
                  {contractorForm.city && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 mb-5 text-sm text-green-700">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span><strong>Great news!</strong> {cityMatchCount || 34} verified contractors are active in {contractorForm.city}.</span>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="flex-1 border border-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-xl hover:bg-slate-50 transition-colors">Back</button>
                    <button onClick={() => { if (validateStep2()) setStep(3); }} className="flex-2 flex-grow bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2">
                      Continue <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Requirements */}
              {step === 3 && (
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">Stand Requirements</h3>
                  <p className="text-slate-600 mb-7">Provide specifications for your exhibition stand</p>
                  <div className="grid gap-4 md:grid-cols-2 mb-6">
                    <Field label="Stand Size" placeholder="e.g., 6m x 8m" value={contractorForm.standSize} onChange={(v) => updateContractorForm('standSize', v)} Icon={Building2} />
                    <SelectField label="Stand Type" value={contractorForm.standType} onChange={(v) => updateContractorForm('standType', v)}
                      options={[{ value: 'inline', label: 'Inline Stand' }, { value: 'corner', label: 'Corner Stand' }, { value: 'island', label: 'Island Stand' }, { value: 'peninsula', label: 'Peninsula Stand' }]} Icon={Building2} />
                    <Field label="Budget (approx.)" placeholder="e.g., 25,000" value={contractorForm.budget} onChange={(v) => updateContractorForm('budget', v)} Icon={DollarSign} />
                    <SelectField label="Currency" value={contractorForm.currency} onChange={(v) => updateContractorForm('currency', v)}
                      options={[{ value: 'USD', label: 'USD ($)' }, { value: 'EUR', label: 'EUR (€)' }, { value: 'GBP', label: 'GBP (£)' }, { value: 'AED', label: 'AED (د.إ)' }]} Icon={DollarSign} />
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Detailed Requirements</label>
                    <textarea className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none" rows={3}
                      placeholder="Describe special features, branding needs, etc." value={contractorForm.requirements} onChange={(e) => updateContractorForm('requirements', e.target.value)} />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex-1 border border-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-xl hover:bg-slate-50 transition-colors">Back</button>
                    <button onClick={() => setStep(4)} className="flex-grow bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2">
                      Continue <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: Contact */}
              {step === 4 && (
                <div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">Your Contact Details</h3>
                  <p className="text-slate-600 mb-7">Almost done! Where should we send the proposals?</p>
                  <div className="grid gap-4 md:grid-cols-2 mb-8">
                    <Field label="Full Name" placeholder="John Smith" value={contractorForm.name} onChange={(v) => updateContractorForm('name', v)} Icon={Users} error={errors.name} />
                    <Field label="Email" type="email" placeholder="john@company.com" value={contractorForm.email} onChange={(v) => updateContractorForm('email', v)} Icon={Mail} error={errors.email} />
                    <Field label="Company" placeholder="Your company name" value={contractorForm.company} onChange={(v) => updateContractorForm('company', v)} Icon={Building2} error={errors.company} />
                    <Field label="Phone" placeholder="+1 (555) 123-4567" value={contractorForm.phone} onChange={(v) => updateContractorForm('phone', v)} Icon={Phone} />
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3 mb-6">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-semibold">You're about to reach {quoteType === 'contractor' ? (cityMatchCount || 12) + ' contractors' : '8 suppliers'}</p>
                      <p className="text-blue-600 mt-0.5">They'll receive your brief and respond with competitive quotes within 24h.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(3)} className="flex-1 border border-slate-200 text-slate-600 font-semibold py-4 px-6 rounded-xl hover:bg-slate-50 transition-colors">Back</button>
                    <button onClick={() => { if (validateStep4()) handleSubmitContractor(); }} disabled={submitting}
                      className="flex-grow bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2">
                      {submitting ? 'Submitting...' : 'Submit Brief & Get Quotes'} <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-3">You'll receive quotes within 24 hours. No spam, ever.</p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* SUPPLIER FORM */}
      {quoteType === 'supplier' && (
      <section className="py-8 bg-slate-50">
        <div className="mx-auto max-w-4xl px-6">
          <motion.div
              key="supplier-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              {step === 1 ? (
                <>
                  <div className="mb-6">
                    <h3 className="text-2xl font-semibold text-slate-900 mb-2">Find Suppliers</h3>
                    <p className="text-slate-600">Select event details and supplier categories</p>
                  </div>

                  <div className="space-y-6">
                    {/* Event Details */}
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Event Information</h4>
                      <div className="grid gap-4 md:grid-cols-3">
                        <Field
                          label="Event Date"
                          type="date"
                          value={supplierForm.eventDate}
                          onChange={(v) => updateSupplierForm('eventDate', v)}
                          Icon={Calendar}
                        />
                        <SelectField
                          label="Country"
                          value={supplierForm.country}
                          onChange={(v) => updateSupplierForm('country', v)}
                          options={countries.map(c => ({ value: c.code, label: c.name }))}
                          Icon={Globe}
                        />
                        <SelectField
                          label="City"
                          value={supplierForm.city}
                          onChange={(v) => updateSupplierForm('city', v)}
                          options={supplierForm.country ? cities[supplierForm.country]?.map(city => ({ value: city, label: city })) || [] : []}
                          Icon={MapPin}
                          disabled={!supplierForm.country}
                        />
                      </div>
                    </div>

                    {/* Supplier Categories */}
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Supplier Categories</h4>
                      <p className="text-sm text-slate-600 mb-4">Select all categories you need suppliers for:</p>
                      <div className="grid gap-3 md:grid-cols-2">
                        {supplierCategories.map((category) => (
                          <label
                            key={category.id}
                            className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                              supplierForm.categories.includes(category.id)
                                ? 'border-purple-600 bg-purple-50'
                                : 'border-slate-200 hover:border-purple-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={supplierForm.categories.includes(category.id)}
                                onChange={() => handleCategoryToggle(category.id)}
                                className="h-4 w-4 text-purple-600 rounded"
                              />
                              <div>
                                <div className="font-medium text-slate-900">{category.name}</div>
                              </div>
                            </div>
                            <div className="text-sm text-slate-500">
                              {category.count} suppliers
                            </div>
                          </label>
                        ))}
                      </div>
                      
                      {supplierForm.categories.length > 0 && (
                        <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
                          <div className="flex items-center gap-2 text-green-800">
                            <CheckCircle2 className="h-5 w-5" />
                            <span className="font-medium">
                              {getSelectedCategoriesCount()} suppliers available in selected categories
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="pt-6 border-t border-slate-200">
                      <button
                        onClick={() => setStep(2)}
                        disabled={supplierForm.categories.length === 0 || !supplierForm.country || !supplierForm.city}
                        className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        Continue to Request Form <ArrowRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <button
                      onClick={() => setStep(1)}
                      className="text-purple-600 hover:text-purple-700 font-medium mb-4"
                    >
                      ← Back to Categories
                    </button>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-2">Supplier Request Form</h3>
                    <p className="text-slate-600">
                      Your request will be sent to {getSelectedCategoriesCount()} suppliers in selected categories
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Contact Information</h4>
                      <div className="grid gap-4 md:grid-cols-2">
                        <Field
                          label="Full Name"
                          placeholder="e.g., Sarah Johnson"
                          value={supplierForm.name}
                          onChange={(v) => updateSupplierForm('name', v)}
                          Icon={Users}
                          error={errors.sup_name}
                        />
                        <Field
                          label="Email"
                          type="email"
                          placeholder="sarah@company.com"
                          value={supplierForm.email}
                          onChange={(v) => updateSupplierForm('email', v)}
                          Icon={Mail}
                          error={errors.sup_email}
                        />
                        <Field
                          label="Company"
                          placeholder="Your company name"
                          value={supplierForm.company}
                          onChange={(v) => updateSupplierForm('company', v)}
                          Icon={Building2}
                          error={errors.sup_company}
                        />
                        <Field
                          label="Phone"
                          placeholder="+1 (555) 123-4567"
                          value={supplierForm.phone}
                          onChange={(v) => updateSupplierForm('phone', v)}
                          Icon={Phone}
                        />
                      </div>
                    </div>

                    {/* Request Details */}
                    <div>
                      <h4 className="text-lg font-semibold text-slate-900 mb-4">Request Details</h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Detailed Requirements
                          </label>
                          <textarea
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                            rows={4}
                            placeholder="Describe what you need from suppliers: quantities, specifications, quality requirements, etc."
                            value={supplierForm.message}
                            onChange={(e) => updateSupplierForm('message', e.target.value)}
                          />
                        </div>
                        
                        <Field
                          label="Required Delivery Date"
                          type="date"
                          value={supplierForm.deliveryDate}
                          onChange={(v) => updateSupplierForm('deliveryDate', v)}
                          Icon={Clock}
                        />

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Attach Files (Optional)
                          </label>
                          <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-slate-400 transition-colors">
                            <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm text-slate-600">
                              Upload floor plans, specifications, or other relevant documents
                            </p>
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => updateSupplierForm('files', Array.from(e.target.files))}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-slate-200">
                      <button
                        onClick={() => { if (validateSupplierSubmit()) handleSubmitSupplier(); }}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                      >
                        Send Request to Suppliers <ArrowRight className="h-5 w-5" />
                      </button>
                      <p className="text-xs text-slate-500 text-center mt-3">
                        Selected suppliers will contact you within 24-48 hours
                      </p>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
        </div>
      </section>
      )}
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text", Icon, disabled = false, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className={`h-4 w-4 ${error ? 'text-red-400' : 'text-slate-400'}`} />
          </div>
        )}
        <input
          type={type}
          className={`w-full rounded-xl border ${error ? 'border-red-400 bg-red-50 focus:ring-red-200' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-200'} ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:ring-2 outline-none disabled:bg-slate-50 disabled:text-slate-500`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
      {error && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, Icon, disabled = false, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
            <Icon className={`h-4 w-4 ${error ? 'text-red-400' : 'text-slate-400'}`} />
          </div>
        )}
        <select
          className={`w-full rounded-xl border ${error ? 'border-red-400 bg-red-50 focus:ring-red-200' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-200'} ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 text-slate-900 focus:ring-2 outline-none disabled:bg-slate-50 disabled:text-slate-500 appearance-none`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><span>⚠</span>{error}</p>}
    </div>
  );
}